// Section 01 — "Your workbook, mapped". A stylized Excel mock of
// Tax Budget FY2026.xlsx beside its ERBI counterparts. Clicking a region on
// EITHER side highlights the pair, draws a connector line between them, and
// explains the mapping in plain English below. Click is the primary
// affordance (hover-only is invisible on a projector); hover merely tints.

import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { FPA_TABS } from './mockData';

const MAPPINGS = [
  {
    id: 'tabs',
    xl: 'The five FP&A template tabs',
    erbi: 'Forecast entry grid',
    caption:
      '“Outside Spend”, “Travel”, “Memberships & Dues”, “Temp Labor”, “All Other” — one row ' +
      'per vendor and purpose, twelve monthly columns. The Forecast tab keeps exactly that ' +
      'shape: the same rows, the same twelve periods, with quarters and annual computed for you.',
  },
  {
    id: 'tree',
    xl: 'Main-sheet hierarchy',
    erbi: 'Dashboard tree',
    caption:
      'Kyle / Clayton / Asif → category → line item. The dashboard renders the same hierarchy, ' +
      'in the same order, as a collapsible tree — and every subtotal is live.',
  },
  {
    id: 'matters',
    xl: 'Column D matter codes',
    erbi: 'Automatic LG classification',
    caption:
      'The matter codes you keep in column D (29122 / 29864 …) become the classifier’s first ' +
      'rule: every Ascent (LG) row whose Text starts with a matter code is matched ' +
      'automatically, every time.',
  },
  {
    id: 'sumif',
    xl: 'SUMIF by description',
    erbi: 'Real links between records',
    caption:
      'Today the Budget columns are SUMIFs keyed on description text — rename one line and the ' +
      'totals silently break. Here, forecast rows point at the budget line itself: rename a ' +
      'line without breaking totals.',
  },
  {
    id: 'actuals',
    xl: 'Hand-keyed quarterly Actuals',
    erbi: 'Computed from your SAP extract',
    caption:
      'Each quarter Sandra reconciles a SAP GL extract and types the Actuals in by hand. Here ' +
      'the totals are computed straight from your SAP extract — debits and credits handled ' +
      'automatically.',
  },
  {
    id: 'prior',
    xl: 'Prior-year columns & comments',
    erbi: 'Preserved, side by side',
    caption:
      'The FY24 / FY25 actual columns and the comments come along unchanged — toggle prior-year ' +
      'columns on the dashboard, keep a comment on every line.',
  },
  {
    id: 'rollup',
    xl: '“By Function” rollup',
    erbi: 'Subtotals + the Slide tab',
    caption:
      '“By Function” — totals by category and Base/Project — is the dashboard’s category and ' +
      'owner subtotal rows: already computed, always current. The “Slide Page” exec summary is ' +
      'the Budget → Slide tab — same groups, spring/fall split, YoY columns, always current.',
  },
];

// Mocked rows from the "FY26 - Actual to Budget" sheet. Hierarchy + the
// columns the mappings point at; numbers echo the worked example.
const SHEET_ROWS = [
  { n: 2, kind: 'owner', a: 'Kyle' },
  { n: 3, kind: 'cat', a: 'Controversy' },
  {
    n: 4, kind: 'line', a: 'IRS Assistance', io: 'TAX1450', bo: 'Base',
    d: '29122 / 29864', bud: '18,000', act: '15,500', py: '61,250',
    com: 'Fenwick + Deloitte', sel: true,
  },
  {
    n: 5, kind: 'line', a: 'Transfer Pricing Rpt — Canada', io: 'TAX1456', bo: 'OT',
    d: '29301', bud: '10,000', act: '12,000', py: '—', com: 'filed in Q1',
  },
  { n: 6, kind: 'owner', a: 'Clayton' },
  { n: 7, kind: 'cat', a: 'Dues & Subscriptions' },
  {
    n: 8, kind: 'line', a: 'Gartner Subscription', io: 'TAX1402', bo: 'Base',
    d: '—', bud: '8,750', act: '8,750', py: '33,200', com: 'annual seat',
  },
];

function Arrow() {
  return (
    <svg
      className="tut-arrow"
      width="26"
      height="10"
      viewBox="0 0 26 10"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M1 5h22M19 1.5 23 5l-4 3.5" />
    </svg>
  );
}

// ── Mini ERBI mocks (visual echoes of the real module, not imports) ─────
function MiniForecast() {
  return (
    <div className="tut-mini-grid">
      <div className="tut-mini-grid-row head">
        <span>Vendor / purpose</span>
        <span>Feb</span>
        <span>Mar</span>
        <span>Apr</span>
        <span>…</span>
        <span className="q">Q1</span>
      </div>
      <div className="tut-mini-grid-row">
        <span>Fenwick — IRS Assistance</span>
        <span className="num">2,000</span>
        <span className="num">2,000</span>
        <span className="num">2,000</span>
        <span>…</span>
        <span className="num q">6,000</span>
      </div>
      <div className="tut-mini-grid-row">
        <span>Deloitte — IRS Assistance</span>
        <span className="num">4,000</span>
        <span className="num">4,000</span>
        <span className="num">4,000</span>
        <span>…</span>
        <span className="num q">12,000</span>
      </div>
    </div>
  );
}

function MiniTree() {
  return (
    <div className="tut-mini-tree">
      <div className="o">
        <span className="tw">▾</span> Kyle <span className="num">27,500</span>
      </div>
      <div className="c">
        <span className="tw">▾</span> Controversy <span className="num">27,500</span>
      </div>
      <div className="l">
        IRS Assistance <span className="num">15,500</span>
      </div>
      <div className="l">
        Transfer Pricing Rpt — Canada <span className="num">12,000</span>
      </div>
    </div>
  );
}

function MiniClassify() {
  return (
    <div className="tut-mini-class">
      <div className="rowline">
        <span className="tut-chip lg code">LG · 29122</span>
        <Arrow />
        <span className="tut-chip line">IRS Assistance</span>
        <span className="tut-chip conf num">100%</span>
      </div>
      <div className="sub">matched automatically, every time</div>
    </div>
  );
}

function MiniRename() {
  return (
    <div className="tut-mini-fk">
      <div className="rename">
        <s>IRS Assistance</s>
        <Arrow />
        <strong>IRS Controversy Support</strong>
      </div>
      <div className="sub">still linked: forecast rows, SAP rows, invoices — totals intact</div>
    </div>
  );
}

function MiniSapSum() {
  return (
    <div className="tut-mini-sap">
      <div className="rowline">
        <span className="tut-chip sap num">+6,000</span>
        <span className="tut-chip sap num">+5,500</span>
        <span className="tut-chip sap acc num">+4,000 accrual</span>
      </div>
      <div className="sum">
        Q1 Actual <strong className="num">15,500</strong> — computed, not typed
      </div>
    </div>
  );
}

function MiniPrior() {
  return (
    <div className="tut-mini-prior">
      <div className="rowline">
        <span className="tut-chip soft">FY25 <span className="num">61,250</span></span>
        <span className="tut-chip soft">FY24 <span className="num">58,400</span></span>
        <span className="tut-chip soft">“Fenwick + Deloitte”</span>
      </div>
      <div className="sub">prior years and comments, kept on every line</div>
    </div>
  );
}

function MiniRollup() {
  return (
    <div className="tut-mini-roll">
      <div className="r">
        <span>Controversy</span>
        <span className="num">27,500</span>
      </div>
      <div className="r">
        <span>Dues &amp; Subscriptions</span>
        <span className="num">8,750</span>
      </div>
      <div className="r total">
        <span>Kyle — total</span>
        <span className="num">36,250</span>
      </div>
    </div>
  );
}

const ERBI_CARDS = [
  { id: 'tabs', title: 'Forecast entry grid', body: <MiniForecast /> },
  { id: 'tree', title: 'Dashboard tree', body: <MiniTree /> },
  { id: 'matters', title: 'Automatic LG classification', body: <MiniClassify /> },
  { id: 'sumif', title: 'Real links, not text matching', body: <MiniRename /> },
  { id: 'actuals', title: 'Actuals from the SAP extract', body: <MiniSapSum /> },
  { id: 'prior', title: 'History preserved', body: <MiniPrior /> },
  { id: 'rollup', title: 'Rollups, computed live', body: <MiniRollup /> },
];

export default function WorkbookMapSection() {
  const [active, setActive] = useState('tabs');
  const wrapRef = useRef(null);
  const leftPanelRef = useRef(null);
  const leftRefs = useRef({});
  const rightRefs = useRef({});
  const [line, setLine] = useState(null);

  const setLeft = (id) => (el) => {
    leftRefs.current[id] = el;
  };
  const setRight = (id) => (el) => {
    rightRefs.current[id] = el;
  };

  const measure = useCallback(() => {
    const wrap = wrapRef.current;
    const panel = leftPanelRef.current;
    const l = leftRefs.current[active];
    const r = rightRefs.current[active];
    if (!wrap || !panel || !l || !r) {
      setLine(null);
      return;
    }
    const wr = wrap.getBoundingClientRect();
    const pr = panel.getBoundingClientRect();
    const lr = l.getBoundingClientRect();
    const rr = r.getBoundingClientRect();
    setLine({
      x1: pr.right - wr.left + 4,
      y1: lr.top + lr.height / 2 - wr.top,
      x2: rr.left - wr.left - 4,
      y2: rr.top + rr.height / 2 - wr.top,
    });
  }, [active]);

  useLayoutEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    // Inter loads with font-display: swap — glyph widths change when it
    // lands, so re-measure or the connector endpoints go stale.
    if (document.fonts?.ready) document.fonts.ready.then(measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  // Region helper: className + click handler for a mapped Excel region.
  const reg = (id, extra = '') =>
    `${extra} tut-xlr${active === id ? ' on' : ''}`.trim();
  const pick = (id) => () => setActive(id);

  const m = MAPPINGS.find((x) => x.id === active);

  return (
    <section id="tut-map" className="tut-section">
      <div className="tut-kicker">01 · Excel, side by side</div>
      <h2>Your workbook, mapped</h2>
      <p className="tut-lead">
        Nothing about how you budget changes — only where it lives. Click any part of the
        workbook (or any card on the right) to see exactly where it goes.
      </p>

      <div className="tut-map-wrap" ref={wrapRef}>
        {line && (
          <svg className="tut-map-svg" aria-hidden="true">
            <path
              d={`M ${line.x1} ${line.y1} C ${line.x1 + 44} ${line.y1}, ${line.x2 - 44} ${line.y2}, ${line.x2} ${line.y2}`}
            />
            <circle cx={line.x1} cy={line.y1} r="4" />
            <circle cx={line.x2} cy={line.y2} r="4" />
          </svg>
        )}

        {/* ── Left: the Excel workbook ───────────────────────────────── */}
        <div className="tut-panel-xl" ref={leftPanelRef}>
          <div className="tut-panel-h">Today — Tax Budget FY2026.xlsx</div>
          <div className="tut-xl">
            <div className="tut-xl-titlebar">
              <span className="tut-xl-dot" />
              Tax Budget FY2026.xlsx — Excel
            </div>
            <div className={reg('sumif', 'tut-xl-formula')} onClick={pick('sumif')}>
              <span className="fx">fx</span>
              <code>=SUMIF(…,"IRS Assistance",…)</code>
            </div>
            <table className="tut-xl-grid">
              <thead>
                <tr>
                  <th className="rh" />
                  {['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'].map((c) => (
                    <th key={c}>{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="rh">1</td>
                  <td className="hdr">Line Item</td>
                  <td className="hdr">IO</td>
                  <td className="hdr">B/OT</td>
                  <td className={reg('matters', 'hdr')} onClick={pick('matters')}>
                    Matter Codes
                  </td>
                  <td className={reg('sumif', 'hdr r')} onClick={pick('sumif')}>
                    Q1 Budget
                  </td>
                  <td className={reg('actuals', 'hdr r')} onClick={pick('actuals')}>
                    Q1 Actual
                  </td>
                  <td className={reg('prior', 'hdr r')} onClick={pick('prior')}>
                    FY25 Act
                  </td>
                  <td className={reg('prior', 'hdr')} onClick={pick('prior')}>
                    Comments
                  </td>
                </tr>
                {SHEET_ROWS.map((row) => (
                  <tr key={row.n}>
                    <td className="rh">{row.n}</td>
                    <td
                      className={reg('tree', `name ${row.kind}`)}
                      onClick={pick('tree')}
                      ref={row.n === 5 ? setLeft('tree') : undefined}
                    >
                      {row.a}
                    </td>
                    <td className="code">{row.io || ''}</td>
                    <td>{row.bo || ''}</td>
                    <td
                      className={reg('matters', 'code')}
                      onClick={pick('matters')}
                      ref={row.n === 4 ? setLeft('matters') : undefined}
                    >
                      {row.d || ''}
                    </td>
                    <td
                      className={reg('sumif', `r num${row.sel ? ' sel' : ''}`)}
                      onClick={pick('sumif')}
                      ref={row.n === 4 ? setLeft('sumif') : undefined}
                    >
                      {row.bud || ''}
                    </td>
                    <td
                      className={reg('actuals', 'r num hand')}
                      onClick={pick('actuals')}
                      ref={row.n === 5 ? setLeft('actuals') : undefined}
                    >
                      {row.act || ''}
                    </td>
                    <td
                      className={reg('prior', 'r num')}
                      onClick={pick('prior')}
                      ref={row.n === 4 ? setLeft('prior') : undefined}
                    >
                      {row.py || ''}
                    </td>
                    <td className={reg('prior', 'com')} onClick={pick('prior')}>
                      {row.com || ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="tut-xl-tabs">
              <span className="tut-xl-tab main">FY26 - Actual to Budget</span>
              <span
                className={`tut-xl-tabgroup tut-xlr${active === 'tabs' ? ' on' : ''}`}
                onClick={pick('tabs')}
                ref={setLeft('tabs')}
              >
                {FPA_TABS.map((t) => (
                  <span key={t} className="tut-xl-tab">
                    {t}
                  </span>
                ))}
              </span>
              <span
                className={`tut-xl-tabgroup tut-xlr${active === 'rollup' ? ' on' : ''}`}
                onClick={pick('rollup')}
                ref={setLeft('rollup')}
              >
                <span className="tut-xl-tab">By Function</span>
                <span className="tut-xl-tab">Slide Page</span>
              </span>
            </div>
          </div>
        </div>

        {/* ── Right: the ERBI counterparts ──────────────────────────── */}
        <div className="tut-panel-erbi">
          <div className="tut-panel-h">In ERBI</div>
          {ERBI_CARDS.map((card) => (
            <div
              key={card.id}
              className={`tut-erbi-card${active === card.id ? ' on' : ''}`}
              onClick={pick(card.id)}
              ref={setRight(card.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault(); // Space must not scroll the page
                  setActive(card.id);
                }
              }}
            >
              <div className="ttl">{card.title}</div>
              {card.body}
            </div>
          ))}
        </div>
      </div>

      <div className="tut-map-caption">
        <div className="t">
          <span className="xl">{m.xl}</span>
          <Arrow />
          <span className="erbi">{m.erbi}</span>
        </div>
        <p>{m.caption}</p>
      </div>

      <div className="tut-goes-away">
        <span className="t">What goes away:</span>
        <s>repairing broken SUMIFs</s>
        <s>copy-pasting GL extracts</s>
        <s>emailing workbook versions</s>
      </div>
    </section>
  );
}
